import React from 'react';
import _ from 'lodash';

import {
  withStyles, createStyles, WithStyles, Theme,
} from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import moment from 'moment';
import locales from '../../../locales';
import { parseDateFromBookingList, parseCreateDateFromBookingList } from '../../../helpers/dateHelpers';
import { parsingMergedTables } from '../../../helpers/bookingHelpers';
import { parsePhoneNumber } from '../../../helpers/reserveHelpers';
import { TReserve } from '../../../types/IBookingStore';
import { primaryColor } from '../../../theme';
import { Duo } from "../../../_FSD/pages/reservesSchemas/ui/duo";
import { SvgIcon } from "../../../_FSD/pages/reservesSchemas/ui/svgIcon";
import { lightTheme } from "../../../_FSD/pages/reservesSchemas/lib/theme";
import { ReactComponent as LightningIcon } from '../../../_FSD/shared/assets/icons/lightning.svg';

const { fg } = lightTheme;
const { badge } = lightTheme.components;

// Цвета статусов как на схеме зала
const getStatusBadgeColor = (state: string): { bg: string; color: string } => {
  switch (state) {
    case 'GUEST_IS_LATE':
      return { bg: '#EA2727', color: '#FFFFFF' }; // DANGER - красный
    case 'NEED_PREPARE_TABLE':
      return { bg: '#EAAA08', color: '#FFFFFF' }; // WARNING - желтый
    case 'AWAIT_PREPAY':
      return { bg: '#EA2727', color: '#FFFFFF' }; // DANGER - красный
    case 'OPEN':
    case 'IN_PROGRESS':
      return { bg: '#1CBD91', color: '#FFFFFF' }; // OPEN - зеленый
    case 'ARRIVED':
      return { bg: '#1CBD91', color: '#FFFFFF' }; // OPEN - зеленый
    default:
      return { bg: '#FFFFFF', color: '#14171F' }; // Белый фон, темный текст
  }
};

const styles = (theme: Theme) => createStyles({
  card: {
    cursor: 'pointer',
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: primaryColor,
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  // Первая строка: дата, время, статус, бейджи
  firstRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    // Не указываем flexDirection здесь, чтобы Material-UI не создавал автоматический медиа-запрос
    // flexDirection будет установлен через inline стили
  },
  // Верхняя часть с временем и статусом
  topSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  timeRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  timeBadge: {
    borderRadius: 8,
    padding: '2px 9px',
    color: '#FFFFFF',
    background: primaryColor,
    display: 'inline-flex !important',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    width: 'auto !important',
    maxWidth: 'fit-content !important',
    alignSelf: 'flex-start',
    '&.MuiBox-root': {
      display: 'inline-flex !important',
      width: 'auto !important',
      maxWidth: 'fit-content !important',
    },
  },
  timeValue: {
    fontWeight: 600,
    fontSize: 14,
  },
  timeDate: {
    fontWeight: 400,
    fontSize: 14,
  },
  statusBadge: {
    borderRadius: 8,
    padding: '2px 9px',
    display: 'inline-flex !important',
    alignItems: 'center',
    flexShrink: 0,
    width: 'auto !important',
    maxWidth: 'fit-content !important',
    alignSelf: 'flex-start',
    '&.MuiBox-root': {
      display: 'inline-flex !important',
      width: 'auto !important',
      maxWidth: 'fit-content !important',
    },
  },
  statusText: {
    fontSize: 12,
    fontWeight: 500,
  },
  banquetBadge: {
    borderRadius: 8,
    padding: '2px 9px',
    backgroundColor: '#F4F4F6',
    color: '#14171F',
    display: 'inline-flex !important',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    width: 'auto !important',
    maxWidth: 'fit-content !important',
    alignSelf: 'flex-start',
    '&.MuiBox-root': {
      display: 'inline-flex !important',
      width: 'auto !important',
      maxWidth: 'fit-content !important',
    },
  },
  banquetIcon: {
    width: 14,
    height: 14,
  },
  banquetText: {
    fontSize: 12,
    fontWeight: 500,
  },
  // Вторая строка: зал/стол, имя/телефон, создан, источник
  secondRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    alignItems: 'start',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'auto 1fr auto auto',
      gap: theme.spacing(2),
    },
  },
  // Имя гостя
  guestName: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  // Телефон
  phoneSection: {
    marginBottom: 0,
  },
  // Блок с именем и телефоном
  guestInfo: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: '1', // Первая колонка на мобильных
    [theme.breakpoints.up('sm')]: {
      gridColumn: '1',
    },
  },
  // Блок с залом и столом
  tableInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    gridColumn: '2', // Вторая колонка на мобильных
    [theme.breakpoints.up('sm')]: {
      gridColumn: '2',
      marginLeft: theme.spacing(3),
    },
  },
  // Количество гостей отдельно
  personsInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  personsIcon: {
    width: 14,
    height: 14,
  },
  personsValue: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  // Блок с датой создания
  createdInfo: {
    gridColumn: '1', // Первая колонка на мобильных
    [theme.breakpoints.up('sm')]: {
      gridColumn: '3',
      textAlign: 'right',
    },
  },
  // Блок с источником
  sourceInfo: {
    gridColumn: '2', // Вторая колонка на мобильных
    [theme.breakpoints.up('sm')]: {
      gridColumn: '4',
      textAlign: 'right',
    },
  },
  phoneText: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  // Информация о столе
  tableInfoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  infoIcon: {
    width: 14,
    height: 14,
  },
  infoText: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  // Третья строка: комментарий
  thirdRow: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid #E5E5E5',
  },
  commentRow: {
    marginBottom: 0,
  },
  additionalInfoLabel: {
    fontSize: 12,
    color: '#AAAAAA',
    fontWeight: 500,
    marginBottom: 4,
  },
  additionalInfoValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
});

interface IProps extends WithStyles<typeof styles> {
  reserve: TReserve;
  onClick: () => void;
}

const ReserveCard: React.FC<IProps> = ({ classes, reserve, onClick }) => {
  // Парсим время и дату отдельно
  const time = parseDateFromBookingList(reserve.date); // HH:mm
  const date = moment(reserve.date).format('DD.MM'); // DD.MM

  // Получаем цвет статуса
  const statusColors = getStatusBadgeColor(reserve.state);
  return (
    <Card className={classes.card} onClick={onClick} elevation={0}>
      <CardContent className={classes.cardContent}>
        {/* Первая строка: дата, время, статус, бейджи */}
        <div
          className={classes.firstRow}
          style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}
        >
          <Box
            className={classes.timeBadge}
            style={{ display: 'inline-flex', width: 'auto', maxWidth: 'fit-content', flexShrink: 0 }}
          >
            <Typography className={classes.timeValue} component="span">
              {time}
            </Typography>
            {date && (
              <Typography className={classes.timeDate} component="span">
                {date}
              </Typography>
            )}
          </Box>
          {reserve.prepay?.amount ? (
            <Box
              className={classes.statusBadge}
              style={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                display: 'inline-flex',
                width: 'auto',
                maxWidth: 'fit-content',
                flexShrink: 0,
              }}
            >
              <Typography className={classes.statusText}>
                {reserve.state === 'AWAIT_PREPAY'
                  ? `Депозит не оплачен: ${reserve.prepay.amount} ₽`
                  : `Депозит: ${reserve.prepay.amount} ₽`}
              </Typography>
            </Box>
          ) : (
            <Box
              className={classes.statusBadge}
              style={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                display: 'inline-flex',
                width: 'auto',
                maxWidth: 'fit-content',
                flexShrink: 0,
              }}
            >
              <Typography className={classes.statusText}>
                {locales.t(`booking.status.${reserve.state}`)}
              </Typography>
            </Box>
          )}
          {reserve.isBanquet && (
            <Box
              className={classes.banquetBadge}
              style={{ display: 'inline-flex', width: 'auto', maxWidth: 'fit-content', flexShrink: 0 }}
            >
              <svg
                className={classes.banquetIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M9.48387 8.12056V12.8274C9.48387 13.0057 9.55524 13.1767 9.68228 13.3028C9.80932 13.4289 9.98163 13.4998 10.1613 13.4998C10.341 13.4998 10.5133 13.4289 10.6403 13.3028C10.7673 13.1767 10.8387 13.0057 10.8387 12.8274V8.73469L11.0148 8.69434C11.86 8.49811 12.614 8.02529 13.1555 7.35201C13.6969 6.67873 13.9944 5.84417 14 4.98268V1.1724C14 0.99407 13.9286 0.823042 13.8016 0.696942C13.6745 0.570842 13.5022 0.5 13.3226 0.5C13.1429 0.5 12.9706 0.570842 12.8436 0.696942C12.7165 0.823042 12.6452 0.99407 12.6452 1.1724V4.98268C12.6452 5.63657 12.3835 6.26367 11.9177 6.72604C11.4518 7.1884 10.8201 7.44816 10.1613 7.44816C9.98163 7.44816 9.80932 7.519 9.68228 7.6451C9.55524 7.7712 9.48387 7.94223 9.48387 8.12056Z"
                  fill="currentColor"
                />
                <path
                  d="M9.93548 5.43095C10.1151 5.43095 10.2875 5.36011 10.4145 5.23401C10.5415 5.10791 10.6129 4.93688 10.6129 4.75855V1.1724C10.6129 0.99407 10.5415 0.823042 10.4145 0.696942C10.2875 0.570842 10.1151 0.5 9.93548 0.5C9.75582 0.5 9.58352 0.570842 9.45648 0.696942C9.32944 0.823042 9.25806 0.99407 9.25806 1.1724V4.75855C9.25806 4.93688 9.32944 5.10791 9.45648 5.23401C9.58352 5.36011 9.75582 5.43095 9.93548 5.43095Z"
                  fill="currentColor"
                />
                <path
                  d="M2.76613 8.68314L2.93548 8.72572V12.155H1.61903C1.44558 12.1507 1.27663 12.21 1.14454 12.3217C1.01245 12.4334 0.926528 12.5895 0.903226 12.7601C0.893729 12.8539 0.904202 12.9486 0.933967 13.0381C0.963731 13.1277 1.01212 13.2099 1.076 13.2797C1.13988 13.3494 1.21781 13.4051 1.30475 13.443C1.39169 13.4809 1.48569 13.5003 1.58065 13.4998H5.60677C5.78022 13.5041 5.94918 13.4447 6.08127 13.3331C6.21335 13.2214 6.29928 13.0653 6.32258 12.8946C6.33208 12.8008 6.3216 12.7061 6.29184 12.6166C6.26207 12.5271 6.21368 12.4448 6.14981 12.3751C6.08593 12.3053 6.00799 12.2497 5.92106 12.2118C5.83412 12.1739 5.74012 12.1545 5.64516 12.155H4.29032V8.73021L4.46194 8.68762C5.24895 8.49875 5.94923 8.05295 6.45043 7.42174C6.95164 6.79053 7.2247 6.01051 7.22581 5.20682V2.29307C7.22581 1.81752 7.03548 1.36145 6.69671 1.02518C6.35793 0.688912 5.89846 0.5 5.41935 0.5H1.80645C1.32735 0.5 0.867873 0.688912 0.529097 1.02518C0.190322 1.36145 0 1.81752 0 2.29307V5.10596C0.00258669 5.92066 0.274667 6.71197 0.774418 7.35825C1.27417 8.00453 1.97393 8.47001 2.76613 8.68314ZM1.35484 2.29307C1.35484 2.17418 1.40242 2.06017 1.48711 1.9761C1.57181 1.89203 1.68668 1.8448 1.80645 1.8448H5.41935C5.53913 1.8448 5.654 1.89203 5.73869 1.9761C5.82339 2.06017 5.87097 2.17418 5.87097 2.29307V5.20682C5.87084 5.51365 5.80724 5.81719 5.68411 6.09863C5.56098 6.38008 5.38095 6.63343 5.15516 6.843C4.73826 7.23353 4.18614 7.45017 3.6129 7.44816H3.4571C2.87681 7.39831 2.33716 7.13187 1.94701 6.7026C1.55686 6.27333 1.34524 5.71316 1.35484 5.13509V2.29307Z"
                  fill="currentColor"
                />
              </svg>
              <Typography className={classes.banquetText} component="span">
                {locales.t('booking.banquet')}
              </Typography>
            </Box>
          )}
          {reserve.isAutoBooking && <Box
            className={classes.statusBadge}
            style={{
              color: fg.default,
              backgroundColor: badge.muted.neutral.bg,
              display: 'inline-flex',
              width: 'auto',
              maxWidth: 'fit-content',
              flexShrink: 0,
            }}
          >
            <Duo
              first={<SvgIcon icon={LightningIcon} width={10} height={14} />}
              second={<span style={{ fontSize: 12, fontWeight: 500 }}>Мгновенный резерв</span>}
              gridColumnGap={4}
              alignItems="center"
            />
          </Box>}
        </div>

        {/* Вторая строка: имя/телефон, зал/стол, создан, источник */}
        <Box className={classes.secondRow}>
          {/* Имя и телефон */}
          <Box className={classes.guestInfo}>
            <Typography className={classes.guestName}>
              {`${reserve.firstName} ${!_.isNull(reserve.lastName) ? reserve.lastName : ''}`}
            </Typography>
            <Box className={classes.phoneSection}>
              <Typography className={classes.phoneText}>
                {parsePhoneNumber(reserve.phone)}
              </Typography>
            </Box>
          </Box>

          {/* Зал и стол */}
          <Box className={classes.tableInfo}>
            {/* Количество гостей */}
            <Box className={classes.personsInfo}>
              <svg
                className={classes.personsIcon}
                width="14"
                height="14"
                viewBox="0 0 10 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.901042 8.01172C3.33619 7.14209 5.99715 7.14209 8.43229 8.01172C8.97243 8.20463 9.3333 8.71617 9.33333 9.28971V10.1667C9.33333 10.9583 8.63198 11.5664 7.84831 11.4544L7.21224 11.3639C5.52375 11.1227 3.80958 11.1227 2.12109 11.3639L1.48503 11.4544C0.701357 11.5664 0 10.9583 0 10.1667V9.28971C2.85423e-05 8.71617 0.360905 8.20463 0.901042 8.01172Z"
                  fill="currentColor"
                />
                <path
                  d="M4.66667 0C6.13943 0 7.33333 1.19391 7.33333 2.66667C7.33333 4.13943 6.13943 5.33333 4.66667 5.33333C3.19391 5.33333 2 4.13943 2 2.66667C2 1.19391 3.19391 0 4.66667 0Z"
                  fill="currentColor"
                />
              </svg>
              <Typography className={classes.personsValue} component="span">
                {reserve.persons}
              </Typography>
            </Box>
            {/* Зал и стол */}
            {reserve.tables && reserve.tables.length > 0 ? (
              <Box className={classes.tableInfoItem}>
                <svg
                  className={classes.infoIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <rect x="3" y="3" width="8" height="8" rx="1" fill="currentColor" />
                  <rect x="12" y="5" width="2" height="4" rx="1" fill="currentColor" />
                  <rect x="5" y="2" width="2" height="4" rx="1" transform="rotate(-90 5 2)" fill="currentColor" />
                  <rect y="5" width="2" height="4" rx="1" fill="currentColor" />
                  <rect x="5" y="14" width="2" height="4" rx="1" transform="rotate(-90 5 14)" fill="currentColor" />
                </svg>
                <Typography className={classes.infoText} component="span">
                  {parsingMergedTables(reserve.tables)}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {/* Создан */}
          <Box className={classes.createdInfo}>
            <Typography className={classes.additionalInfoLabel}>
              СОЗДАН
            </Typography>
            <Typography className={classes.additionalInfoValue}>
              {parseCreateDateFromBookingList(reserve.createdDate)}
            </Typography>
          </Box>

          {/* Источник */}
          <Box className={classes.sourceInfo}>
            <Typography className={classes.additionalInfoLabel}>
              ИСТОЧНИК
            </Typography>
            <Typography className={classes.additionalInfoValue}>
              {reserve.source.name}
            </Typography>
          </Box>
        </Box>

        {/* Третья строка: комментарий */}
        {reserve.comment && (
          <Box className={classes.thirdRow}>
            <Box className={classes.commentRow}>
              <Typography className={classes.additionalInfoLabel}>
                КОММЕНТАРИЙ
              </Typography>
              <Typography className={classes.additionalInfoValue}>
                {reserve.comment}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default withStyles(styles)(ReserveCard);
